import { DatePipe } from '@angular/common';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import {
  ApplicationConfig,
  effect,
  ErrorHandler,
  importProvidersFrom,
  inject,
  Injector,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { provideRouter, withComponentInputBinding, withPreloading } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideTransloco } from '@jsverse/transloco';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { LumberjackLog, provideLumberjack } from '@ngworker/lumberjack';
import { provideLumberjackConsoleDriver } from '@ngworker/lumberjack/console-driver';
import { RxReactiveFormsModule } from '@rxweb/reactive-form-validators';
import {
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  includeBearerTokenInterceptor,
  provideKeycloak
} from 'keycloak-angular';
import { environment as Config } from '@env/environment';
import { AppRoutes } from '@app/app.routes';
import { RxjsUtils } from '@shared/utility/rxjs-utils';
import { AppErrorHandlerService } from '@core/common/services/app-error-handler';
import { initApp, provideCustomReuseStrategy } from '@core/core.providers';
import { provideDisableBackButton } from '@core/disable-back-button';
import { formatWithPattern, UiFormats } from '@core/formatting';
import { BaseHttpInterceptor } from '@core/interceptors/base-http-inteceptor';
import { CustomPreloadingStrategy } from '@core/router/custom-preloading-strategy';
import { getSettingsAsync, silentCheckSsoRedirectUri } from '@core/sso/keycloak-server-config';
import { StoreEffects, StoreReducers } from '@core/state/store-config';
import { ProvideTanstack } from '@core/tanstack/tanstack.providers';
import TranslocoConfig from '@core/transloco/transloco-loader';

const keycloakConfig = await getSettingsAsync();

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([BaseHttpInterceptor, includeBearerTokenInterceptor])),
    provideTransloco(TranslocoConfig),
    provideStore(StoreReducers),
    provideEffects(StoreEffects),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
      trace: isDevMode(),
      traceLimit: 25,
      serialize: {
        options: {
          map: true
        }
      }
    }),
    provideRouterStore(),
    DatePipe,
    importProvidersFrom(Title, RxjsUtils, RxReactiveFormsModule),
    provideLumberjackConsoleDriver(),
    provideDisableBackButton(),
    provideRouter(AppRoutes, withPreloading(CustomPreloadingStrategy), withComponentInputBinding()),
    provideCustomReuseStrategy(),
    provideKeycloak({
      config: keycloakConfig,
      initOptions: {
        pkceMethod: 'S256',
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: silentCheckSsoRedirectUri(),
        silentCheckSsoFallback: false
      }
    }),
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [{ urlPattern: /^.*$/ }]
    },
    provideServiceWorker('main-sw.js', {
      enabled: !isDevMode(),
      // Registra il ServiceWorker non appena l'applicazione è stabile
      // o dopo 30 secondi, a seconda di quale condizione si verifica per prima.
      registrationStrategy: 'registerWhenStable:30000'
    }),
    ProvideTanstack,
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: AppErrorHandlerService },
    // Configura eCharts e Bryntum con la lingua della sessione precedente (localStorage)
    // prima che l'applicazione disegni la prima schermata. Fallback 'it' se nessuna preferenza è salvata.
    // Il caricamento Transloco e l'attivazione completa avvengono in LanguageActivation dopo il login.
    provideAppInitializer(async () => {
      const [{ rawLanguageCode }, { normalizeLanguageCode }, languageGlobal] = await Promise.all([
        import('@core/language/current-language'),
        import('@core/language/normalize-locale'),
        import('@core/language/language-global')
      ]);
      languageGlobal.setLanguage(normalizeLanguageCode(rawLanguageCode()));
    }),
    // Globale Luxon allineato alla Lingua: i token di testo (nomi mese/giorno, marcatore AM/PM)
    // seguono la Lingua, non il Formato regionale (che governa struttura e numeri). L'effect reagisce
    // a login, cambio lingua e reload fra schede del browser.
    provideAppInitializer(async () => {
      const injector = inject(Injector);
      const [{ currentLanguageCode }, { setLocale }] = await Promise.all([
        import('@core/language/current-language'),
        import('@core/formatting/date/engine')
      ]);
      effect(
        () => {
          setLocale(currentLanguageCode());
        },
        { injector }
      );
    }),
    provideAppInitializer(async () => await import('@core/ag-grid/ag-grid-conf')),
    provideAppInitializer(initApp),
    provideLumberjack({
      levels: Config.loggerLevel,
      format: ({ scope, createdAt: timestamp, level, message }: LumberjackLog) => {
        const upperLevel = level.toUpperCase();
        const ts = formatWithPattern(timestamp, { pattern: UiFormats.DATE_TIME_WITH_SECONDS });
        return `${ts} ${upperLevel}${scope ? ` [${scope}]` : ''} ${message}`;
      }
    })
  ]
};
